import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Box,
  MenuItem,
} from "@mui/material";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import { useMasterSetting } from "../../../Hooks/useMasterSetting";

const MasterSettingModal = ({ open, onClose, settingData, timeList = [], onSuccess }) => {
  const [settingInfo, setSettingInfo] = useState({
    setting_name: "",
    setting_value: "",
    status: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { updateMasterSetting } = useMasterSetting();

  useEffect(() => {
    if (settingData) {
      setSettingInfo({
        setting_name: settingData.setting_name,
        setting_value: settingData.setting_value,
        status: settingData.status,
      });
    }
  }, [settingData, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSettingInfo((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleStatusChange = (event) => {
    setSettingInfo((prevData) => ({
      ...prevData,
      status: event.target.checked ? 1 : 0,
    }));
  };

  const validateForm = () => {
    if (!settingInfo.setting_name.trim()) {
      showNotification("error", "Setting Name is required");
      return false;
    }
    if (!String(settingInfo.setting_value).trim()) {
      showNotification("error", "Setting Value is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const result = await updateMasterSetting(settingData.id, settingInfo);

      if (result.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Edit Master Setting</DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography
            component="div"
            color="secondary"
            variant="caption"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            Field marked * is required
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="setting_name"
                label="Setting Name"
                name="setting_name"
                value={settingInfo.setting_name}
                onChange={handleChange}
                variant="outlined"
                autoFocus
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                required
                fullWidth
                id="setting_value"
                label="Setting Value (in minutes)"
                name="setting_value"
                value={settingInfo.setting_value}
                onChange={handleChange}
                variant="outlined"
              >
                {timeList.map((time) => (
                  <MenuItem key={time.id} value={time.name}>
                    {time.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingInfo.status === 1}
                    onChange={handleStatusChange}
                    name="status"
                    color="primary"
                  />
                }
                label={
                  settingInfo.status === 1
                    ? "Setting is Active"
                    : "Setting is Inactive"
                }
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: "none" }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            backgroundColor: "#7152F3",
            textTransform: "none",
            "&:hover": { backgroundColor: "#5c34fa" },
          }}
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterSettingModal;
